import type { QuizCohort, QuizPreparation, QuizQuestionByPriorAddon } from "types/";
import { HEAT_SHADES, type HeatShade } from "../../analyticsTheme";
import { LOW_SAMPLE, isLowSample } from "./metrics";

export type PrepRow = { key: string; item_index: number; label: string | null };

/** One row per question (all publications together, the admin preview included), in question order. */
export const prepGrid = (
  questions: QuizQuestionByPriorAddon[]
): { rows: PrepRow[]; addons: string[] } => {
  const rows = new Map<number, PrepRow>();
  const weight = new Map<number, number>();
  const addons = new Set<string>();
  for (const q of questions) {
    const answers = q.answers_used_before + q.answers_not_used_before;
    // An item edited since has one row per wording: show the most answered one.
    if (!rows.has(q.item_index) || answers > (weight.get(q.item_index) ?? 0)) {
      rows.set(q.item_index, { key: String(q.item_index), item_index: q.item_index, label: q.label ?? null });
      weight.set(q.item_index, answers);
    }
    addons.add(q.addon);
  }
  return {
    rows: [...rows.values()].sort((a, b) => a.item_index - b.item_index),
    addons: [...addons],
  };
};

export type PrepSide = { correct: number; answers: number };
export type PrepDelta = { delta: number; used: PrepSide; notUsed: PrepSide; low: boolean };

/** Success-rate gap (points) between takers who used the add-on before answering and those who did not. */
export const prepDelta = (
  questions: QuizQuestionByPriorAddon[],
  itemIndex: number,
  addon: string
): PrepDelta | null => {
  // Publications and wordings of the same question are added up.
  const rows = questions.filter((x) => x.addon === addon && x.item_index === itemIndex);
  const sum = (pick: (q: QuizQuestionByPriorAddon) => number) =>
    rows.reduce((total, q) => total + pick(q), 0);
  const used = { correct: sum((q) => q.correct_used_before), answers: sum((q) => q.answers_used_before) };
  const notUsed = {
    correct: sum((q) => q.correct_not_used_before),
    answers: sum((q) => q.answers_not_used_before),
  };
  if (used.answers === 0 || notUsed.answers === 0) return null;
  return {
    delta: Math.round((used.correct / used.answers - notUsed.correct / notUsed.answers) * 100),
    used,
    notUsed,
    low: isLowSample(used.answers) || isLowSample(notUsed.answers),
  };
};

export type BestPriorAddon = { addon: string; score: number; baseline: number };

/** Prior add-on with the best quiz score, next to the quiz-first score; null unless both have enough viewers. */
export const bestPriorAddon = (
  prep: QuizPreparation | null
): BestPriorAddon | null => {
  const base = prep?.quiz_first;
  if (!prep || base?.avg_score == null || base.viewers < LOW_SAMPLE)
    return null;
  let best: BestPriorAddon | null = null;
  for (const c of prep.prior_addons) {
    if (c.avg_score == null || c.viewers < LOW_SAMPLE) continue;
    if (!best || c.avg_score > best.score)
      best = { addon: c.cohort, score: c.avg_score, baseline: base.avg_score };
  }
  return best;
};

/** Two-line label: the name, then the viewer count. An empty or missing cohort reads "no viewer". */
export const cohortLabel = (
  name: string,
  cohort: QuizCohort | undefined,
  viewers: (count: number) => string,
  noViewer: string
): string[] => [
  name,
  cohort && cohort.viewers > 0 ? viewers(cohort.viewers) : noViewer,
];

/** Rounded average, or null (no bar) for an empty cohort: never an invented 0. */
export const cohortScore = (cohort: QuizCohort | undefined): number | null =>
  cohort && cohort.viewers > 0 && cohort.avg_score != null
    ? Math.round(cohort.avg_score)
    : null;

export const deltaShade = (delta: number): HeatShade => {
  if (delta >= 20) return HEAT_SHADES.best;
  if (delta >= 10) return HEAT_SHADES.better;
  if (delta >= 3) return HEAT_SHADES.good;
  if (delta > -3) return HEAT_SHADES.neutral;
  if (delta > -10) return HEAT_SHADES.bad;
  return HEAT_SHADES.worst;
};
